import {
	BreadcrumbItemType,
	BreadcrumbSeparatorType,
} from 'antd/es/breadcrumb/Breadcrumb';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { AppService } from '../services/AppService';
import { LoggerService } from '../services/LoggerService';
import { FINANCIAL_INSTITUTION_ID } from '../constants/session';
import { getFinancialInstitutionDomain } from 'helpers/environment';

interface IAppContextProps {
	breadcrumb?: Partial<BreadcrumbItemType & BreadcrumbSeparatorType>[];
	setBreadcrumb: (
		breadcrumb:
			| Partial<BreadcrumbItemType & BreadcrumbSeparatorType>[]
			| undefined,
	) => void;

	disabledAuthLogo: boolean;
	setDisabledAuthLogo: (disableAuthLogo: boolean) => void;

	isLoading: boolean;
	setIsLoading: (isLoading: boolean) => void;

	isAppLoading: boolean;
	financialInstitutionId: string;
}

export const AppContext = createContext<IAppContextProps>({
	breadcrumb: undefined,
	setBreadcrumb: () => {
		throw new Error('Método não implementado');
	},
	disabledAuthLogo: false,
	setDisabledAuthLogo: () => {
		throw new Error('Método não implementado');
	},
	isLoading: false,
	setIsLoading: () => {
		throw new Error('Método não implementado');
	},
	isAppLoading: false,
	financialInstitutionId: '',
});

interface IAppProviderProps {
	children?: React.ReactNode;
}

export const AppProvider = ({ children }: IAppProviderProps) => {
	const [breadcrumb, setBreadcrumb] = useState<
		Partial<BreadcrumbItemType & BreadcrumbSeparatorType>[] | undefined
	>();
	const [disabledAuthLogo, setDisabledAuthLogo] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [isAppLoading, setIsAppLoading] = useState<boolean>(true);
	const [financialInstitutionId, setFinancialInstitutionId] =
		useState<string>('');

	useEffect(() => {
		AppService.getFinancialInstitution(getFinancialInstitutionDomain())
			.then(({ id }) => {
				sessionStorage.setItem(FINANCIAL_INSTITUTION_ID, id);
				setFinancialInstitutionId(id);
				setIsAppLoading(false);
			})
			.catch(e => {
				LoggerService.error(e ?? 'AppProvider');
				setIsAppLoading(false);
			});
	}, []);

	return (
		<AppContext.Provider
			value={{
				breadcrumb,
				setBreadcrumb,
				disabledAuthLogo,
				setDisabledAuthLogo,
				isLoading,
				setIsLoading,
				isAppLoading,
				financialInstitutionId,
			}}
		>
			{children}
		</AppContext.Provider>
	);
};

export const useBreadcrumb = () => {
	const context = useContext(AppContext);

	if (context === undefined) {
		throw new Error('useAppContext must be used within a AppProvider');
	}

	return {
		breadcrumb: context.breadcrumb,
		setBreadcrumb: context.setBreadcrumb,
	};
};

export const useAuthLogo = () => {
	const context = useContext(AppContext);

	if (context === undefined) {
		throw new Error('useAppContext must be used within a AppProvider');
	}

	return {
		isAuthLogoVisible: !context.disabledAuthLogo,
		showAuthLogo: () => context.setDisabledAuthLogo(false),
		hideAuthLogo: () => context.setDisabledAuthLogo(true),
	};
};
